import React from 'react';
import { Text, Switch, PillToggle } from '@chatsdet/ui';
import type { AssertionSeverity } from '@chatsdet/types';
import { MacroRichTextField } from './MacroRichTextField';
import { SEVERITY_OPTIONS, AssertionSeverityHelp } from './AssertionSeverityControls';

type AssertionNodeConfigProps = {
  focusKey: number;
  localData: Record<string, any>;
  setLocalData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
};

export const AssertionNodeConfig: React.FC<AssertionNodeConfigProps> = ({
  focusKey,
  localData,
  setLocalData,
}) => {
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  // Auto-focus the condition textarea when node changes (focusKey increments), but only if empty
  React.useEffect(() => {
    const condition = ((localData.condition as string | undefined) ?? '').trim();
    if (!condition) {
      const timer = setTimeout(() => {
        wrapperRef.current?.querySelector('textarea')?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [focusKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Derive severity from strict for backward compat with existing configs
  const severity: AssertionSeverity =
    localData.severity ?? (localData.strict ? 'fail' : 'warn');

  return (
    <div className="space-y-4">
      <div ref={wrapperRef}>
        <MacroRichTextField
          multiline
          label="Condition"
          placeholder="e.g., page.title contains 'Dashboard' (type $ for macros)"
          value={localData.condition || ''}
          onChange={(value) => setLocalData({ ...localData, condition: value })}
          description="What the agent should verify on the page before continuing."
          rows={3}
        />
      </div>
      <div className="border-t border-border/40 py-3 space-y-2">
        <Text size="xs" className="text-muted-foreground font-medium">
          Severity
        </Text>
        <PillToggle
          options={SEVERITY_OPTIONS}
          value={severity}
          onChange={(value) =>
            setLocalData({ ...localData, severity: value, strict: undefined })
          }
        />
        <AssertionSeverityHelp severity={severity} />
      </div>
      <div className="border-t border-border/40 py-3 space-y-1">
        <Switch
          size="sm"
          isSelected={localData.alwaysUseAgent ?? false}
          onChange={(isSelected) => setLocalData({ ...localData, alwaysUseAgent: isSelected })}
          label={<Text size="xs" className="text-muted-foreground">Always use agent</Text>}
        />
        <Text size="xs" className="text-muted-foreground">
          Skip the cache and use the AI agent to evaluate this assertion every time.
        </Text>
      </div>
    </div>
  );
};
